import * as classNames from "classnames";
import * as React from "react";
import Hyperlink from "source/app/presentation/Shared/Link/Hyperlink";
import { useLongPress } from "../../Hooks/useLongPress";
import { useHoldCharge } from "../../Hooks/useHoldCharge";
import * as styles from "./EmotionCharacterButton.css";

export function EmotionCharacterButton({
    isActive,
    classes,
    handleClick,
    handleLongPress,
    children,
}: {
    isActive: boolean;
    classes?: string;
    handleClick: () => void;
    handleLongPress?: () => void;
    children: React.ReactNode;
}) {
    const [isPressed, setIsPressed] = React.useState(false);
    const pointerIdRef = React.useRef<number | undefined>(undefined);
    const longPress = useLongPress(handleLongPress);
    const surfaceRef = useHoldCharge(longPress.isHolding, !!handleLongPress);
    const cancelPress = (suppressClick: boolean) => {
        longPress.cancel(suppressClick);
        setIsPressed(false);
    };
    React.useEffect(() => {
        if (handleLongPress && !longPress.isHolding) {
            setIsPressed(false);
        }
    }, [handleLongPress, longPress.isHolding]);

    return (
        <Hyperlink
            classes={classes}
            ariaLabel={handleLongPress
                ? "I: tap to record, hold for two seconds to record I+ max score"
                : undefined}
            whenClicked={() => {
                if (!longPress.consumeClick()) {
                    handleClick();
                }
            }}
        >
            <span
                ref={surfaceRef}
                className={classNames(styles.surface, {
                    [styles.active]: isActive,
                    [styles.pressed]: isPressed,
                    [styles.holdable]: !!handleLongPress,
                })}
                data-emotion-hold-control={handleLongPress ? "I" : undefined}
                data-holding={longPress.isHolding ? "true" : undefined}
                onPointerDown={(event) => {
                    if (event.button !== 0 || !event.isPrimary) {
                        return;
                    }
                    pointerIdRef.current = event.pointerId;
                    event.currentTarget.setPointerCapture(event.pointerId);
                    longPress.start();
                    setIsPressed(true);
                }}
                onPointerMove={(event) => {
                    if (pointerIdRef.current !== event.pointerId) {
                        return;
                    }
                    const bounds = event.currentTarget.getBoundingClientRect();
                    if (event.clientX < bounds.left - 12 || event.clientX > bounds.right + 12 ||
                        event.clientY < bounds.top - 12 || event.clientY > bounds.bottom + 12) {
                        cancelPress(true);
                    }
                }}
                onPointerUp={(event) => {
                    if (pointerIdRef.current === event.pointerId) {
                        pointerIdRef.current = undefined;
                        cancelPress(false);
                    }
                }}
                onPointerCancel={() => {
                    pointerIdRef.current = undefined;
                    cancelPress(true);
                }}
                onLostPointerCapture={() => {
                    if (pointerIdRef.current !== undefined) {
                        pointerIdRef.current = undefined;
                        cancelPress(true);
                    }
                }}
                onContextMenu={(event) => {
                    if (handleLongPress) {
                        event.preventDefault();
                    }
                }}
            >
                {handleLongPress && <span className={styles.chargeGlow} aria-hidden="true" />}
                {children}
            </span>
        </Hyperlink>
    );
}
